import { API, API_BASE } from '../../config/api';
import { useState, useRef } from 'react';
import { api } from '../../context/AuthContext';
import { HomeFeatureStar } from './HomeFeatureStar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MoreVertical, Upload, Loader2, Music, FileAudio, Mic, Volume2 } from 'lucide-react';
import { PreviewModeToggle } from './PreviewModeToggle';


const SALE_STATUSES = [
  { value: 'for_sale', label: 'For Sale', color: 'text-[#22C55E]', bg: 'bg-[#22C55E]/10 border-[#22C55E]' },
  { value: 'lease_only', label: 'Lease Only', color: 'text-[#DAA520]', bg: 'bg-[#DAA520]/10 border-[#DAA520]' },
  { value: 'not_for_sale', label: 'Not For Sale', color: 'text-[#FF3B30]', bg: 'bg-[#FF3B30]/10 border-[#FF3B30]' },
];

export function BeatsTab({ beats, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVoiceTag, setUploadingVoiceTag] = useState(false);
  const [applyVoiceTag, setApplyVoiceTag] = useState(true);
  const [voiceTagSource, setVoiceTagSource] = useState('site'); // 'site' | 'custom'
  const [customVoiceTag, setCustomVoiceTag] = useState('');
  const [siteVoiceTag, setSiteVoiceTag] = useState('');
  const coverRef = useRef(null);
  const audioRef = useRef(null);
  const voiceTagRef = useRef(null);
  const [form, setForm] = useState({
    title: '', producer: '', description: '', cover_image: '', audio_preview: '',
    bpm: '', key: '', genre: '', tags: [],
    license_basic: 0, license_premium: 0, license_exclusive: 0, is_sold: false, sale_status: 'for_sale',
    on_sale: false, sale_price_basic: '', sale_price_premium: '', sale_price_exclusive: ''
  });

  const resetForm = () => {
    setForm({
      title: '', producer: '', description: '', cover_image: '', audio_preview: '',
      bpm: '', key: '', genre: '', tags: [],
      license_basic: 0, license_premium: 0, license_exclusive: 0, is_sold: false, sale_status: 'for_sale',
      on_sale: false, sale_price_basic: '', sale_price_premium: '', sale_price_exclusive: ''
    });
    setEditing(null);
    setApplyVoiceTag(true);
    setVoiceTagSource('site');
    setCustomVoiceTag('');
  };

  // Fetch site-wide voice tag
  useState(() => {
    api.get(`${API}/site-settings`).then(({ data }) => {
      if (data?.voice_tag_url) setSiteVoiceTag(data.voice_tag_url);
    }).catch(() => {});
  });

  const openEdit = (beat) => {
    setForm({
      title: beat.title || '', producer: beat.producer || '', description: beat.description || '',
      cover_image: beat.cover_image || '', audio_preview: beat.audio_preview || '',
      bpm: beat.bpm || '', key: beat.key || '', genre: beat.genre || '',
      tags: beat.tags || [],
      license_basic: beat.license_basic || 0, license_premium: beat.license_premium || 0,
      license_exclusive: beat.license_exclusive || 0, is_sold: beat.is_sold || false,
      sale_status: beat.sale_status || 'for_sale',
      on_sale: beat.on_sale || false,
      sale_price_basic: beat.sale_price_basic || '', sale_price_premium: beat.sale_price_premium || '', sale_price_exclusive: beat.sale_price_exclusive || ''
    });
    setEditing(beat);
    setDialogOpen(true);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, cover_image: `${API_BASE}${data.url}` }));
      toast.success('Cover uploaded!');
    } catch { toast.error('Failed to upload cover'); }
    finally { setUploadingCover(false); }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAudio(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, audio_preview: `${API_BASE}${data.url}` }));
      toast.success('Audio uploaded!');
    } catch { toast.error('Failed to upload audio'); }
    finally { setUploadingAudio(false); }
  };

  const handleVoiceTagUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVoiceTag(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCustomVoiceTag(`${API_BASE}${data.url}`);
      setVoiceTagSource('custom');
      toast.success('Voice tag uploaded!');
    } catch { toast.error('Failed to upload voice tag'); }
    finally { setUploadingVoiceTag(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      bpm: form.bpm ? parseInt(form.bpm) : null,
      sale_price_basic: form.sale_price_basic !== '' ? parseFloat(form.sale_price_basic) || null : null,
      sale_price_premium: form.sale_price_premium !== '' ? parseFloat(form.sale_price_premium) || null : null,
      sale_price_exclusive: form.sale_price_exclusive !== '' ? parseFloat(form.sale_price_exclusive) || null : null,
      apply_voice_tag: applyVoiceTag,
      custom_voice_tag: voiceTagSource === 'custom' ? customVoiceTag : '',
    };
    try {
      if (editing) {
        await api.put(`${API}/beats/${editing.id}`, payload);
        toast.success('Beat updated!');
      } else {
        await api.post(`${API}/beats`, payload);
        toast.success('Beat created!');
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Failed to save beat';
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this beat?')) return;
    try {
      await api.delete(`${API}/beats/${id}`);
      toast.success('Beat deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  const cycleSaleStatus = async (beat) => {
    const order = ['for_sale', 'lease_only', 'not_for_sale'];
    const current = beat.sale_status || 'for_sale';
    const nextIdx = (order.indexOf(current) + 1) % order.length;
    const next = order[nextIdx];
    const label = SALE_STATUSES.find(s => s.value === next)?.label || next;
    try {
      await api.put(`${API}/beats/${beat.id}`, { sale_status: next });
      toast.success(`Beat set to "${label}"`);
      onRefresh();
    } catch { toast.error('Failed to update status'); }
  };

  return (
    <div className="space-y-6" data-testid="beats-content">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="font-cabinet text-xl sm:text-2xl font-black uppercase tracking-tighter">BEAT LICENSING</h2>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black font-black text-sm px-6 py-3 rounded-none animate-pulse hover:animate-none" data-testid="add-beat-btn">
              <Plus className="w-5 h-5 mr-2" /> ADD BEAT
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-cabinet text-xl font-black uppercase">
                {editing ? 'EDIT BEAT' : 'ADD NEW BEAT'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" required data-testid="beat-title-input" />
                </div>
                <div>
                  <Label className="overline">Producer</Label>
                  <Input value={form.producer} onChange={(e) => setForm(prev => ({ ...prev, producer: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="beat-producer-input" />
                </div>
              </div>
              <div>
                <Label className="overline">Description</Label>
                <textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] p-3 text-white resize-none outline-none" rows={2}
                  data-testid="beat-description-input" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="overline">BPM</Label>
                  <Input type="number" value={form.bpm} onChange={(e) => setForm(prev => ({ ...prev, bpm: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="140" data-testid="beat-bpm-input" />
                </div>
                <div>
                  <Label className="overline">Key</Label>
                  <Input value={form.key} onChange={(e) => setForm(prev => ({ ...prev, key: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="C Minor" data-testid="beat-key-input" />
                </div>
                <div>
                  <Label className="overline">Genre</Label>
                  <Input value={form.genre} onChange={(e) => setForm(prev => ({ ...prev, genre: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="Hip Hop" data-testid="beat-genre-input" />
                </div>
              </div>
              {/* Cover Image */}
              <div>
                <Label className="overline">Cover Image</Label>
                <div className="mt-2 flex gap-2">
                  <Input value={form.cover_image} onChange={(e) => setForm(prev => ({ ...prev, cover_image: e.target.value }))}
                    className="flex-1 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="URL or upload" />
                  <input type="file" ref={coverRef} accept="image/*" onChange={handleCoverUpload} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => coverRef.current?.click()} disabled={uploadingCover}
                    className="rounded-none border-[#00B4D8] bg-[#00B4D8]/10 text-[#00B4D8] hover:bg-[#00B4D8] hover:text-black font-bold px-4">
                    {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" />UPLOAD</>}
                  </Button>
                </div>
              </div>
              {/* Audio Preview */}
              <div>
                <Label className="overline">Audio Preview</Label>
                <div className="mt-2 flex gap-2">
                  <Input value={form.audio_preview} onChange={(e) => setForm(prev => ({ ...prev, audio_preview: e.target.value }))}
                    className="flex-1 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="Upload MP3 preview" />
                  <input type="file" ref={audioRef} accept="audio/*" onChange={handleAudioUpload} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => audioRef.current?.click()} disabled={uploadingAudio}
                    className="rounded-none border-[#00B4D8] bg-[#00B4D8]/10 text-[#00B4D8] hover:bg-[#00B4D8] hover:text-black font-bold px-4">
                    {uploadingAudio ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileAudio className="w-4 h-4 mr-1" />UPLOAD</>}
                  </Button>
                </div>
              </div>
              {/* Voice Tag */}
              {form.audio_preview && (
              <div className="border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 p-4 space-y-3" data-testid="voice-tag-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4 text-[#8B5CF6]" />
                    <Label className="overline text-[#8B5CF6]">Voice Tag</Label>
                  </div>
                  <Switch checked={applyVoiceTag} onCheckedChange={setApplyVoiceTag} data-testid="voice-tag-toggle" />
                </div>
                {applyVoiceTag && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setVoiceTagSource('site')}
                        className={`flex-1 px-3 py-2 text-xs font-bold uppercase border transition-all ${voiceTagSource === 'site' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'border-[#333] text-[#999] hover:border-[#8B5CF6]'}`}
                        data-testid="voice-tag-site-btn">
                        <Volume2 className="w-3.5 h-3.5 inline mr-1" />USE MY SAVED TAG
                      </button>
                      <button type="button" onClick={() => setVoiceTagSource('custom')}
                        className={`flex-1 px-3 py-2 text-xs font-bold uppercase border transition-all ${voiceTagSource === 'custom' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'border-[#333] text-[#999] hover:border-[#8B5CF6]'}`}
                        data-testid="voice-tag-custom-btn">
                        <Upload className="w-3.5 h-3.5 inline mr-1" />UPLOAD NEW TAG
                      </button>
                    </div>
                    {voiceTagSource === 'site' && siteVoiceTag && (
                      <div className="flex items-center gap-2 bg-[#0A0A0A] p-2 border border-[#333]">
                        <Mic className="w-3.5 h-3.5 text-[#8B5CF6] shrink-0" />
                        <span className="text-xs text-[#ccc] flex-1">Site voice tag loaded</span>
                        <audio src={`${API_BASE}${siteVoiceTag}`} controls className="h-8" style={{ maxWidth: '180px' }} />
                      </div>
                    )}
                    {voiceTagSource === 'site' && !siteVoiceTag && (
                      <p className="text-xs text-[#FF3B30]">No site voice tag set. Go to Site Settings to upload one, or upload a custom tag here.</p>
                    )}
                    {voiceTagSource === 'custom' && (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input type="file" ref={voiceTagRef} accept="audio/*" onChange={handleVoiceTagUpload} className="hidden" />
                          <Button type="button" onClick={() => voiceTagRef.current?.click()} disabled={uploadingVoiceTag}
                            className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/80 text-white font-bold px-4 rounded-none"
                            data-testid="upload-voice-tag-btn">
                            {uploadingVoiceTag ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                            UPLOAD VOICE TAG
                          </Button>
                          {customVoiceTag && <span className="text-xs text-[#22C55E] self-center">Uploaded!</span>}
                        </div>
                        {customVoiceTag && (
                          <div className="flex items-center gap-2 bg-[#0A0A0A] p-2 border border-[#333]">
                            <Mic className="w-3.5 h-3.5 text-[#8B5CF6] shrink-0" />
                            <span className="text-xs text-[#ccc] flex-1">Custom voice tag</span>
                            <audio src={customVoiceTag} controls className="h-8" style={{ maxWidth: '180px' }} />
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-[10px] text-[#666]">Voice tag will be overlaid every 30 seconds on the beat preview</p>
                  </div>
                )}
              </div>
              )}
              {/* License Pricing */}
              <div className="border-t border-[#262626] pt-4">
                <Label className="overline mb-3 block text-[#FF3B30]">License Pricing (USD)</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-[#A3A3A3]">Basic Lease</Label>
                    <Input type="number" step="0.01" min="0" value={form.license_basic}
                      onChange={(e) => setForm(prev => ({ ...prev, license_basic: parseFloat(e.target.value) || 0 }))}
                      className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="beat-basic-price" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3]">Premium Lease</Label>
                    <Input type="number" step="0.01" min="0" value={form.license_premium}
                      onChange={(e) => setForm(prev => ({ ...prev, license_premium: parseFloat(e.target.value) || 0 }))}
                      className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="beat-premium-price" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3]">Exclusive</Label>
                    <Input type="number" step="0.01" min="0" value={form.license_exclusive}
                      onChange={(e) => setForm(prev => ({ ...prev, license_exclusive: parseFloat(e.target.value) || 0 }))}
                      className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="beat-exclusive-price" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Label className="overline mr-2">Availability</Label>
                {SALE_STATUSES.map(s => (
                  <button key={s.value} type="button"
                    onClick={() => setForm(prev => ({ ...prev, sale_status: s.value }))}
                    className={`px-3 py-1.5 text-xs font-bold uppercase border transition-colors ${form.sale_status === s.value ? s.bg + ' border ' + s.color : 'border-[#262626] text-[#A3A3A3]'}`}
                    data-testid={`beat-status-${s.value}`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="border-t border-[#262626] pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <Switch checked={form.on_sale} onCheckedChange={(checked) => setForm(prev => ({ ...prev, on_sale: checked }))} data-testid="beat-sale-switch" />
                  <Label className="overline text-[#DAA520]">On Sale</Label>
                </div>
                {form.on_sale && (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-[#A3A3A3]">Sale Basic</Label>
                      <Input type="number" step="0.01" min="0" value={form.sale_price_basic}
                        onChange={(e) => setForm(prev => ({ ...prev, sale_price_basic: parseFloat(e.target.value) || 0 }))}
                        className="mt-1 bg-[#0A0A0A] border-[#DAA520]/30 rounded-none text-[#DAA520] text-sm" placeholder="$" data-testid="beat-sale-basic" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#A3A3A3]">Sale Premium</Label>
                      <Input type="number" step="0.01" min="0" value={form.sale_price_premium}
                        onChange={(e) => setForm(prev => ({ ...prev, sale_price_premium: parseFloat(e.target.value) || 0 }))}
                        className="mt-1 bg-[#0A0A0A] border-[#DAA520]/30 rounded-none text-[#DAA520] text-sm" placeholder="$" data-testid="beat-sale-premium" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#A3A3A3]">Sale Exclusive</Label>
                      <Input type="number" step="0.01" min="0" value={form.sale_price_exclusive}
                        onChange={(e) => setForm(prev => ({ ...prev, sale_price_exclusive: parseFloat(e.target.value) || 0 }))}
                        className="mt-1 bg-[#0A0A0A] border-[#DAA520]/30 rounded-none text-[#DAA520] text-sm" placeholder="$" data-testid="beat-sale-exclusive" />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="beat-submit-btn">
                  {editing ? 'UPDATE BEAT' : 'ADD BEAT'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-[#262626]">CANCEL</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {beats.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] p-12 text-center">
          <Music className="w-12 h-12 mx-auto text-[#A3A3A3] mb-4" />
          <p className="text-[#A3A3A3]">No beats yet. Add your first beat for licensing!</p>
        </div>
      ) : (
        <div className="bg-[#141414] border border-[#262626] table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Beat</th>
                <th>Producer</th>
                <th>BPM / Key</th>
                <th>Basic</th>
                <th>Premium</th>
                <th>Exclusive</th>
                <th>Preview</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {beats.map(beat => (
                <tr key={beat.id} data-testid={`beat-item-${beat.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      {beat.cover_image && <img src={beat.cover_image} alt="" className="w-10 h-10 object-cover" />}
                      <div className="flex items-center gap-2">
                        <div>
                          <span className="font-medium">{beat.title}</span>
                          {beat.genre && <p className="text-xs text-[#A3A3A3]">{beat.genre}</p>}
                        </div>
                        <HomeFeatureStar item={beat} endpoint="beats" onToggled={() => onRefresh()} />
                      </div>
                    </div>
                  </td>
                  <td className="text-[#A3A3A3]">{beat.producer}</td>
                  <td className="text-[#A3A3A3] font-mono text-sm">{beat.bpm || '-'} / {beat.key || '-'}</td>
                  <td className="font-mono">{beat.license_basic > 0 ? `$${beat.license_basic}` : '-'}</td>
                  <td className="font-mono">{beat.license_premium > 0 ? `$${beat.license_premium}` : '-'}</td>
                  <td className="font-mono">{beat.license_exclusive > 0 ? `$${beat.license_exclusive}` : '-'}</td>
                  <td>
                    <PreviewModeToggle
                      value={{ preview_mode: beat.preview_mode || 'full', unlock_price: beat.unlock_price || 0 }}
                      onChange={async (payload) => {
                        await api.put(`${API}/beats/${beat.id}`, payload, { withCredentials: true });
                        onRefresh();
                      }}
                      testid={`beat-${beat.id}`}
                    />
                  </td>
                  <td>
                    <button onClick={() => cycleSaleStatus(beat)}
                      className={`text-xs font-bold uppercase px-3 py-1 border transition-colors cursor-pointer ${
                        (beat.sale_status || 'for_sale') === 'for_sale' ? 'text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/10' :
                        (beat.sale_status || 'for_sale') === 'lease_only' ? 'text-[#DAA520] border-[#DAA520]/30 bg-[#DAA520]/10' :
                        'text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10'
                      }`}
                      data-testid={`beat-toggle-${beat.id}`}
                      title="Click to cycle status">
                      {(beat.sale_status || 'for_sale') === 'for_sale' ? 'FOR SALE' :
                       (beat.sale_status || 'for_sale') === 'lease_only' ? 'LEASE ONLY' : 'NOT FOR SALE'}
                    </button>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(beat)}
                        className="px-3 py-1.5 text-xs font-bold uppercase bg-[#00B4D8] hover:bg-[#00B4D8]/80 text-black transition-all"
                        data-testid={`beat-edit-${beat.id}`}>
                        <Edit className="w-3.5 h-3.5 inline mr-1" />EDIT
                      </button>
                      <button onClick={() => handleDelete(beat.id)}
                        className="px-3 py-1.5 text-xs font-bold uppercase bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white transition-all"
                        data-testid={`beat-delete-${beat.id}`}>
                        <Trash2 className="w-3.5 h-3.5 inline mr-1" />DEL
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
